import { Button, InputAdornment } from '@mui/material'
import React, { forwardRef, useState } from 'react'
import { IconButton, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { tLabel, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

export type PasswordResetFormProps = {
  onSubmitPasswordReset: (inputs: PasswordResetFormValues) => Promise<void>
  onOpenModal: () => void
}

export type PasswordResetFormValues = {
  password: string
}

const schema: UseFormProps['schema'] = o => ({
  password: o.string().min(8).max(30).label(tLabel('passwordReset.password')),
})

const defaultValues: PasswordResetFormValues = {
  password: '',
}

export const PasswordResetForm: Organism<PasswordResetFormProps> = ({
  onSubmitPasswordReset,
  onOpenModal,
}) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<PasswordResetFormValues>({
    schema,
    defaultValues,
  })

  return (
    <Form onSubmit={handleSubmit(onSubmitPasswordReset)}>
      <PasswordInputCell error={errors?.password} {...register('password')} />
      <ButtonCell loading={isSubmitting} />
    </Form>
  )
}

const PasswordInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't' | 'type'>, ref) => {
    const { t } = useTranslation('login')
    const [showPassword, setShowPassword] = useState(false)

    return (
      <CellFrame>
        <TextInputField
          type={showPassword ? 'text' : 'password'}
          {...props}
          textFieldProps={{
            ...props.textFieldProps,
            InputProps: {
              ...props.textFieldProps?.InputProps,
              inputProps: { variant: 'body2', sx: { px: 4, py: 3 } },
              endAdornment: (
                <InputAdornment position="end">
                  {showPassword ? (
                    <IconButton.Display
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  ) : (
                    <IconButton.Hide
                      onClick={() => setShowPassword(prevState => !prevState)}
                    />
                  )}
                </InputAdornment>
              ),
            },
          }}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('passwordReset')

  return (
    <CellFrame
      sx={{
        py: 2,
        textAlign: 'center',
      }}
    >
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}
